import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { 
  ArrowLeft, Camera, Edit2, Trash2, Plus, 
  Activity, Calendar, DollarSign, FileText, 
  Image, Send, Syringe, Scissors, Stethoscope,
  Clock, MapPin, Trophy, User, Package, Wheat, X,
  History, Plane, Baby, Heart, Skull, QrCode, Archive, RotateCcw, Lock, Search,
  ChevronLeft, ChevronRight, ZoomIn
} from 'lucide-react';

// Import form components from extracted file
import { 
  FeedForm, HistoryEventForm, HealthForm, RideForm, EventForm, ExpenseForm,
  COUNTRIES, frequencyLabels, RIDE_TYPE_OPTIONS 
} from '../components/horse-profile/Forms';

export const HorseProfile = () => {
  const { horseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { canAddEdit, requireSubscription } = useSubscription();
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const healthPhotoInputRef = useRef(null);
  
  const [horse, setHorse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingHealthPhoto, setUploadingHealthPhoto] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  
  // Data states
  const [healthRecords, setHealthRecords] = useState([]);
  const [rideLogs, setRideLogs] = useState([]);
  const [eventLogs, setEventLogs] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [gear, setGear] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [feedEntries, setFeedEntries] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [showPrep, setShowPrep] = useState({ items: [] });
  const [newShowPrepItem, setNewShowPrepItem] = useState('');
  const [riders, setRiders] = useState([]);
  
  // Photo lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Dialog states
  const [healthDialogOpen, setHealthDialogOpen] = useState(false);
  const [rideDialogOpen, setRideDialogOpen] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [deceasedDialogOpen, setDeceasedDialogOpen] = useState(false);
  const [deceasedConfirmStep, setDeceasedConfirmStep] = useState(1);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [gearDialogOpen, setGearDialogOpen] = useState(false);
  const [editGearDialogOpen, setEditGearDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  
  // Edit dialog states
  const [editHealthDialogOpen, setEditHealthDialogOpen] = useState(false);
  const [editRideDialogOpen, setEditRideDialogOpen] = useState(false);
  const [editEventDialogOpen, setEditEventDialogOpen] = useState(false);
  const [editExpenseDialogOpen, setEditExpenseDialogOpen] = useState(false);
  const [editFeedDialogOpen, setEditFeedDialogOpen] = useState(false);
  const [editHistoryDialogOpen, setEditHistoryDialogOpen] = useState(false);
  
  // Form states
  const [newHealth, setNewHealth] = useState({ record_type: 'vaccination', provider: '', date: '', next_due_date: '', notes: '', vet_visit_type: null });
  const [newRide, setNewRide] = useState({ rider: '', date: '', ride_type: '', duration: '', notes: '' });
  const [newEvent, setNewEvent] = useState({ event_name: '', location: '', discipline: '', event_class: '', placing: '', score: '', date: '', notes: '' });
  const [newExpense, setNewExpense] = useState({ category: '', amount: '', vendor: '', date: '', notes: '' });
  const [newFeed, setNewFeed] = useState({ date: '', items: [], notes: '' });
  const [newHistoryEvent, setNewHistoryEvent] = useState({ event_type: '', date: '', from_country: '', to_country: '', trainer: '', foaling_details: null, owner_name: '', description: '', notes: '' });
  const [deceasedDate, setDeceasedDate] = useState('');
  const [editData, setEditData] = useState({});
  const [transferEmail, setTransferEmail] = useState('');
  
  // Editing item states
  const [editingHealth, setEditingHealth] = useState(null);
  const [editingRide, setEditingRide] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingFeed, setEditingFeed] = useState(null);
  const [editingHistoryEvent, setEditingHistoryEvent] = useState(null);
  const [feedSaveAsNew, setFeedSaveAsNew] = useState(false);
  
  // Gear and Document form states
  const [newGear, setNewGear] = useState({ item_name: '', tack_type: '', size: '', serial_number: '', purchase_date: '', purchase_location: '', cost: '' });
  const [editingGear, setEditingGear] = useState(null);
  const [newDocument, setNewDocument] = useState({ title: '', document_type: 'registration', custom_type: '' });
  const gearReceiptInputRef = useRef(null);
  const gearPhotoInputRef = useRef(null);
  const documentFileInputRef = useRef(null);
  const [selectedGearReceiptFile, setSelectedGearReceiptFile] = useState(null);
  const [selectedGearPhotoFile, setSelectedGearPhotoFile] = useState(null);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState(null);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const fetchHorse = async () => {
    const headers = getHeaders();
    try {
      const response = await fetch(`${API}/horses/${horseId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setHorse(data);
        setEditData(data);
      } else {
        toast.error('Horse not found');
        navigate('/horses');
      }
    } catch (error) {
      console.error('Failed to fetch horse:', error);
    }
  };

  const fetchAllData = async () => {
    const headers = getHeaders();
    // Add cache-busting parameter to force fresh data
    const cacheBuster = `&_t=${Date.now()}`;
    try {
      const [healthRes, ridesRes, eventsRes, expensesRes, gearRes, docsRes, photosRes, feedRes, ridersRes] = await Promise.all([
        fetch(`${API}/health-records?horse_id=${horseId}${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/ride-logs?horse_id=${horseId}${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/event-logs?horse_id=${horseId}${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/expenses?horse_id=${horseId}${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/gear?horse_id=${horseId}${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/documents?horse_id=${horseId}${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/photos?horse_id=${horseId}${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/feed-entries?horse_id=${horseId}${cacheBuster}`, { headers, cache: 'no-store' }),
        fetch(`${API}/rider-profiles?_t=${Date.now()}`, { headers, cache: 'no-store' })
      ]);

      if (healthRes.ok) setHealthRecords(await healthRes.json());
      if (ridesRes.ok) setRideLogs(await ridesRes.json());
      if (eventsRes.ok) setEventLogs(await eventsRes.json());
      if (expensesRes.ok) setExpenses(await expensesRes.json());
      if (gearRes.ok) setGear(await gearRes.json());
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (photosRes.ok) setPhotos(await photosRes.json());
      if (feedRes.ok) setFeedEntries(await feedRes.json());
      if (ridersRes.ok) setRiders(await ridersRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const fetchShowPrep = async () => {
    const headers = getHeaders();
    try {
      const response = await fetch(`${API}/show-prep/${horseId}`, { headers });
      if (response.ok) {
        setShowPrep(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch show prep:', error);
    }
  };

  const fetchTimeline = async () => {
    const headers = getHeaders();
    try {
      const response = await fetch(`${API}/horses/${horseId}/timeline`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTimeline(data.timeline || []);
      }
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchHorse();
      await fetchAllData();
      await fetchTimeline();
      await fetchShowPrep();
      setLoading(false);
    };
    loadData();
  }, [horseId, token]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const uploadHeaders = {};
    if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
    if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;

    try {
      const response = await fetch(`${API}/upload/horses/${horseId}`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setHorse({ ...horse, profile_photo: data.file_url });
        toast.success('Photo updated!');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const handleUpdateHorse = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/horses/${horseId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const data = await response.json();
        setHorse(data);
        setEditDialogOpen(false);
        toast.success('Horse updated!');
      }
    } catch (error) {
      toast.error('Failed to update horse');
    }
  };

  const handleDeleteHorse = async () => {
    try {
      const response = await fetch(`${API}/horses/${horseId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success('Horse deleted');
        navigate('/horses');
      }
    } catch (error) {
      toast.error('Failed to delete horse');
    }
  };

  const handleArchiveHorse = async () => {
    try {
      const response = await fetch(`${API}/horses/${horseId}/archive`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setHorse(data);
        toast.success('Horse archived to Former');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to archive horse');
      }
    } catch (error) {
      toast.error('Failed to archive horse');
    }
  };

  const handleReinstateHorse = async () => {
    try {
      const response = await fetch(`${API}/horses/${horseId}/reinstate`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setHorse(data);
        toast.success('Horse reinstated');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reinstate horse');
      }
    } catch (error) {
      toast.error('Failed to reinstate horse');
    }
  };

  // Check if a record is read-only (from previous owner)
  const isReadOnly = (record) => record?.read_only === true;

  // Health Records CRUD
  const handleAddHealth = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/health-records`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...newHealth, horse_id: horseId })
      });

      if (response.ok) {
        const data = await response.json();
        setHealthRecords([data, ...healthRecords]);
        setHealthDialogOpen(false);
        setNewHealth({ record_type: 'vaccination', provider: '', date: '', next_due_date: '', notes: '', vet_visit_type: null });
        toast.success('Health record added!');
      }
    } catch (error) {
      toast.error('Failed to add health record');
    }
  };

  // Upload photo for veterinary health record
  const handleHealthPhotoUpload = async (recordId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      
      setUploadingHealthPhoto(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
        const uploadHeaders = {};
        if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
        if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
        
        const response = await fetch(`${API}/upload/health-photo/${recordId}`, {
          method: 'POST',
          headers: uploadHeaders,
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          // Update the editing health record with new photos
          if (editingHealth && editingHealth.record_id === recordId) {
            setEditingHealth({ ...editingHealth, photos: data.photos });
          }
          // Update health records list
          setHealthRecords(healthRecords.map(r => 
            r.record_id === recordId ? { ...r, photos: data.photos } : r
          ));
          toast.success('Photo added!');
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to upload photo');
        }
      } catch (error) {
        toast.error('Failed to upload photo');
      } finally {
        setUploadingHealthPhoto(false);
      }
    };
    input.click();
  };

  const handleEditHealth = async (e) => {
    e.preventDefault();
    if (!editingHealth) return;
    try {
      const response = await fetch(`${API}/health-records/${editingHealth.record_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ ...editingHealth, horse_id: horseId })
      });

      if (response.ok) {
        const data = await response.json();
        setHealthRecords(healthRecords.map(r => r.record_id === data.record_id ? data : r));
        setEditHealthDialogOpen(false);
        setEditingHealth(null);
        toast.success('Health record updated!');
      }
    } catch (error) {
      toast.error('Failed to update health record');
    }
  };

  const handleDeleteHealth = async (recordId) => {
    try {
      const response = await fetch(`${API}/health-records/${recordId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        setHealthRecords(healthRecords.filter(r => r.record_id !== recordId));
        toast.success('Record deleted');
      } else {
        toast.error('Failed to delete record');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Ride Logs CRUD
  const handleAddRide = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/ride-logs`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...newRide, horse_id: horseId, duration: newRide.duration ? parseInt(newRide.duration) : null })
      });

      if (response.ok) {
        const data = await response.json();
        setRideLogs([...rideLogs, data]);
        setRideDialogOpen(false);
        setNewRide({ rider: '', date: '', ride_type: '', duration: '', notes: '' });
        toast.success('Ride logged!');
      }
    } catch (error) {
      toast.error('Failed to log ride');
    }
  };

  const handleEditRide = async (e) => {
    e.preventDefault();
    if (!editingRide) return;
    try {
      const response = await fetch(`${API}/ride-logs/${editingRide.ride_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ ...editingRide, horse_id: horseId, duration: editingRide.duration ? parseInt(editingRide.duration) : null })
      });

      if (response.ok) {
        const data = await response.json();
        setRideLogs(rideLogs.map(r => r.ride_id === data.ride_id ? data : r));
        setEditRideDialogOpen(false);
        setEditingRide(null);
        toast.success('Ride updated!');
      }
    } catch (error) {
      toast.error('Failed to update ride');
    }
  };

  const handleDeleteRide = async (rideId) => {
    try {
      const response = await fetch(`${API}/ride-logs/${rideId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        setRideLogs(rideLogs.filter(r => r.ride_id !== rideId));
        toast.success('Ride deleted');
      } else {
        toast.error('Failed to delete ride');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Event Logs CRUD
  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/event-logs`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...newEvent, horse_id: horseId })
      });

      if (response.ok) {
        const data = await response.json();
        setEventLogs([...eventLogs, data]);
        setEventDialogOpen(false);
        setNewEvent({ event_name: '', location: '', discipline: '', event_class: '', placing: '', score: '', date: '', notes: '' });
        toast.success('Event added!');
      }
    } catch (error) {
      toast.error('Failed to add event');
    }
  };

  const handleEditEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;
    try {
      const response = await fetch(`${API}/event-logs/${editingEvent.event_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ ...editingEvent, horse_id: horseId })
      });

      if (response.ok) {
        const data = await response.json();
        setEventLogs(eventLogs.map(e => e.event_id === data.event_id ? data : e));
        setEditEventDialogOpen(false);
        setEditingEvent(null);
        toast.success('Event updated!');
      }
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const response = await fetch(`${API}/event-logs/${eventId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        setEventLogs(eventLogs.filter(e => e.event_id !== eventId));
        toast.success('Event deleted');
      } else {
        toast.error('Failed to delete event');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Expenses CRUD
  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/expenses`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...newExpense, horse_id: horseId, amount: parseFloat(newExpense.amount) })
      });

      if (response.ok) {
        const data = await response.json();
        setExpenses([...expenses, data]);
        setExpenseDialogOpen(false);
        setNewExpense({ category: '', amount: '', vendor: '', date: '', notes: '' });
        toast.success('Expense added!');
      }
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleEditExpense = async (e) => {
    e.preventDefault();
    if (!editingExpense) return;
    try {
      const response = await fetch(`${API}/expenses/${editingExpense.expense_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ ...editingExpense, horse_id: horseId, amount: parseFloat(editingExpense.amount) })
      });

      if (response.ok) {
        const data = await response.json();
        setExpenses(expenses.map(e => e.expense_id === data.expense_id ? data : e));
        setEditExpenseDialogOpen(false);
        setEditingExpense(null);
        toast.success('Expense updated!');
      }
    } catch (error) {
      toast.error('Failed to update expense');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      const response = await fetch(`${API}/expenses/${expenseId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        setExpenses(expenses.filter(e => e.expense_id !== expenseId));
        toast.success('Expense deleted');
      } else {
        toast.error('Failed to delete expense');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Show Prep handlers
  const handleToggleShowPrepItem = async (itemId) => {
    const updatedItems = showPrep.items.map(item => 
      item.item_id === itemId ? { ...item, checked: !item.checked } : item
    );
    setShowPrep({ ...showPrep, items: updatedItems });
    
    try {
      await fetch(`${API}/show-prep/${horseId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ items: updatedItems })
      });
    } catch (error) {
      toast.error('Failed to update checklist');
    }
  };

  const handleAddShowPrepItem = async () => {
    if (!newShowPrepItem.trim()) return;
    
    try {
      const response = await fetch(`${API}/show-prep/${horseId}/add-item`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ name: newShowPrepItem.trim() })
      });
      
      if (response.ok) {
        const newItem = await response.json();
        setShowPrep({ ...showPrep, items: [...showPrep.items, newItem] });
        setNewShowPrepItem('');
        toast.success('Item added!');
      }
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleDeleteShowPrepItem = async (itemId) => {
    try {
      const response = await fetch(`${API}/show-prep/${horseId}/item/${itemId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (response.ok) {
        setShowPrep({ ...showPrep, items: showPrep.items.filter(item => item.item_id !== itemId) });
        toast.success('Item removed');
      }
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleClearShowPrepChecklist = async () => {
    try {
      const response = await fetch(`${API}/show-prep/${horseId}/clear`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (response.ok) {
        const updated = await response.json();
        setShowPrep(updated);
        toast.success('All items unchecked');
      }
    } catch (error) {
      toast.error('Failed to clear checklist');
    }
  };

  // Feed CRUD
  const handleAddFeed = async (e) => {
    e.preventDefault();
    if (!newFeed.items || newFeed.items.length === 0) {
      toast.error('Please add at least one feed item');
      return;
    }
    try {
      const response = await fetch(`${API}/feed-entries`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...newFeed, horse_id: horseId })
      });

      if (response.ok) {
        const data = await response.json();
        setFeedEntries([data, ...feedEntries]); // Add to top
        setFeedDialogOpen(false);
        setNewFeed({ date: '', items: [], notes: '' });
        toast.success('Feed entry added!');
      }
    } catch (error) {
      toast.error('Failed to add feed entry');
    }
  };

  const handleEditFeed = async (e) => {
    e.preventDefault();
    if (!editingFeed) return;
    
    try {
      if (feedSaveAsNew) {
        // Create new entry with today's date
        const today = new Date().toISOString().split('T')[0];
        const response = await fetch(`${API}/feed-entries`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            horse_id: horseId,
            date: today,
            items: editingFeed.items,
            notes: editingFeed.notes
          })
        });

        if (response.ok) {
          const data = await response.json();
          setFeedEntries([data, ...feedEntries]); // Add to top
          setEditFeedDialogOpen(false);
          setEditingFeed(null);
          setFeedSaveAsNew(false);
          toast.success('New feed schedule created!');
        }
      } else {
        // Update existing entry
        const response = await fetch(`${API}/feed-entries/${editingFeed.feed_id}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify({
            date: editingFeed.date,
            items: editingFeed.items,
            notes: editingFeed.notes
          })
        });

        if (response.ok) {
          const data = await response.json();
          setFeedEntries(feedEntries.map(f => f.feed_id === data.feed_id ? data : f));
          setEditFeedDialogOpen(false);
          setEditingFeed(null);
          toast.success('Feed entry updated!');
        }
      }
    } catch (error) {
      toast.error('Failed to save feed entry');
    }
  };

  const handleDeleteFeed = async (feedId) => {
    try {
      const response = await fetch(`${API}/feed-entries/${feedId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        setFeedEntries(feedEntries.filter(f => f.feed_id !== feedId));
        toast.success('Feed entry deleted');
      } else {
        toast.error('Failed to delete feed entry');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // History Event CRUD
  const handleAddHistoryEvent = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/history-events`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...newHistoryEvent, horse_id: horseId })
      });

      if (response.ok) {
        setHistoryDialogOpen(false);
        setNewHistoryEvent({ event_type: '', date: '', from_country: '', to_country: '', trainer: '', foaling_details: null, owner_name: '', description: '', notes: '' });
        fetchTimeline();
        toast.success('Event added to history!');
      }
    } catch (error) {
      toast.error('Failed to add history event');
    }
  };

  const handleEditHistoryEvent = async (e) => {
    e.preventDefault();
    if (!editingHistoryEvent) return;
    try {
      // If no event_id, this is a new event (from auto-generated transfer) - create it
      if (!editingHistoryEvent.event_id) {
        const response = await fetch(`${API}/history-events`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            horse_id: horseId,
            event_type: editingHistoryEvent.event_type,
            date: editingHistoryEvent.date,
            from_country: editingHistoryEvent.from_country,
            to_country: editingHistoryEvent.to_country,
            trainer: editingHistoryEvent.trainer,
            owner_name: editingHistoryEvent.owner_name,
            foaling_details: editingHistoryEvent.foaling_details,
            description: editingHistoryEvent.description,
            notes: editingHistoryEvent.notes,
            transfer_id: editingHistoryEvent.transfer_id // Link to transfer so auto-generated is hidden
          })
        });

        if (response.ok) {
          setEditHistoryDialogOpen(false);
          setEditingHistoryEvent(null);
          fetchTimeline();
          toast.success('Event saved!');
        } else {
          toast.error('Failed to save event');
        }
      } else {
        // Update existing event
        const response = await fetch(`${API}/history-events/${editingHistoryEvent.event_id}`, {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(editingHistoryEvent)
        });

        if (response.ok) {
          setEditHistoryDialogOpen(false);
          setEditingHistoryEvent(null);
          fetchTimeline();
          toast.success('Event updated!');
        } else {
          toast.error('Failed to update event');
        }
      }
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleDeleteHistoryEvent = async (eventId) => {
    try {
      const response = await fetch(`${API}/history-events/${eventId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        fetchTimeline();
        toast.success('Event deleted');
      } else {
        toast.error('Failed to delete history event');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleMarkDeceased = async () => {
    if (!deceasedDate) {
      toast.error('Please enter a date');
      return;
    }
    if (deceasedConfirmStep === 1) {
      setDeceasedConfirmStep(2);
      return;
    }
    try {
      const response = await fetch(`${API}/horses/${horseId}/mark-deceased?deceased_date=${deceasedDate}`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setHorse(data);
        setDeceasedDialogOpen(false);
        setDeceasedDate('');
        setDeceasedConfirmStep(1);
        fetchTimeline();
        toast.success('Horse marked as deceased and archived');
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleUnmarkDeceased = async () => {
    try {
      const response = await fetch(`${API}/horses/${horseId}/unmark-deceased`, {
        method: 'POST',
        headers: getHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setHorse(data);
        setRestoreDialogOpen(false);
        fetchTimeline();
        toast.success('Horse restored and deceased status removed');
      }
    } catch (error) {
      toast.error('Failed to restore horse');
    }
  };

  // Photo gallery upload handler
  const handleGalleryPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check max 20 photos limit
    if (photos.length >= 20) {
      toast.error('Maximum 20 photos allowed. Please delete some photos to add more.');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Step 1: Create photo record first
      const createResponse = await fetch(`${API}/photos`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ horse_id: horseId, caption: photoCaption || null })
      });

      if (!createResponse.ok) {
        const error = await createResponse.json();
        toast.error(error.detail || 'Failed to create photo record');
        return;
      }

      const photoRecord = await createResponse.json();

      // Step 2: Upload the file to the photo record
      const formData = new FormData();
      formData.append('file', file);

      const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
      const uploadHeaders = {};
      if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
      if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;

      const uploadResponse = await fetch(`${API}/upload/photos/${photoRecord.photo_id}`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData
      });

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        // Update photo record with the file URL
        const updatedPhoto = { ...photoRecord, photo_url: uploadData.file_url };
        setPhotos([updatedPhoto, ...photos]); // Add to top (newest first)
        setPhotoCaption('');
        toast.success('Photo uploaded!');
      } else {
        const error = await uploadResponse.json();
        toast.error(error.detail || 'Failed to upload photo');
        // Clean up the created photo record if upload failed
        await fetch(`${API}/photos/${photoRecord.photo_id}`, { method: 'DELETE', headers: getHeaders() });
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const response = await fetch(`${API}/photos/${photoId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        setPhotos(photos.filter(p => p.photo_id !== photoId));
        toast.success('Photo deleted');
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/transfers`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ horse_id: horseId, to_user_email: transferEmail })
      });

      if (response.ok) {
        setTransferDialogOpen(false);
        setTransferEmail('');
        toast.success('Transfer request sent!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to initiate transfer');
      }
    } catch (error) {
      toast.error('Failed to initiate transfer');
    }
  };

  // Add Gear handler
  const handleAddGear = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/gear`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ 
          ...newGear, 
          horse_id: horseId,
          cost: newGear.cost ? parseFloat(newGear.cost) : null 
        })
      });
      if (response.ok) {
        const gearItem = await response.json();
        
        const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
        const uploadHeaders = {};
        if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
        if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
        
        // Upload receipt if selected
        if (selectedGearReceiptFile) {
          const formData = new FormData();
          formData.append('file', selectedGearReceiptFile);
          await fetch(`${API}/upload/gear-receipt/${gearItem.gear_id}`, {
            method: 'POST',
            headers: uploadHeaders,
            body: formData
          });
        }
        
        // Upload photo if selected
        if (selectedGearPhotoFile) {
          const formData = new FormData();
          formData.append('file', selectedGearPhotoFile);
          await fetch(`${API}/upload/gear/${gearItem.gear_id}`, {
            method: 'POST',
            headers: uploadHeaders,
            body: formData
          });
        }
        
        setGear([gearItem, ...gear]);
        setGearDialogOpen(false);
        setNewGear({ item_name: '', tack_type: '', size: '', serial_number: '', purchase_date: '', purchase_location: '', cost: '' });
        setSelectedGearReceiptFile(null);
        setSelectedGearPhotoFile(null);
        toast.success('Gear added!');
      }
    } catch (error) {
      toast.error('Failed to add gear');
    }
  };

  // Edit Gear handler
  const handleEditGear = async (e) => {
    e.preventDefault();
    if (!editingGear) return;
    try {
      const response = await fetch(`${API}/gear/${editingGear.gear_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          item_name: editingGear.item_name,
          tack_type: editingGear.tack_type || null,
          size: editingGear.size || null,
          serial_number: editingGear.serial_number || null,
          purchase_date: editingGear.purchase_date || null,
          purchase_location: editingGear.purchase_location || null,
          cost: editingGear.cost ? parseFloat(editingGear.cost) : null,
          horse_id: horseId
        })
      });
      if (response.ok) {
        const updated = await response.json();
        setGear(gear.map(g => g.gear_id === updated.gear_id ? updated : g));
        setEditGearDialogOpen(false);
        setEditingGear(null);
        toast.success('Tack item updated!');
      } else {
        toast.error('Failed to update tack');
      }
    } catch (error) {
      toast.error('Failed to update tack');
    }
  };

  // Add Document handler
  const handleAddDocument = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/documents`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...newDocument, horse_id: horseId })
      });
      if (response.ok) {
        const doc = await response.json();
        
        const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
        const uploadHeaders = {};
        if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
        if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;
        
        // Upload file if selected
        if (selectedDocumentFile) {
          const formData = new FormData();
          formData.append('file', selectedDocumentFile);
          await fetch(`${API}/upload/documents/${doc.document_id}`, {
            method: 'POST',
            headers: uploadHeaders,
            body: formData
          });
        }
        
        setDocuments([doc, ...documents]);
        setDocumentDialogOpen(false);
        setNewDocument({ title: '', document_type: 'registration' });
        setSelectedDocumentFile(null);
        toast.success('Document added!');
      }
    } catch (error) {
      toast.error('Failed to add document');
    }
  };

  // Delete Gear handler
  const handleDeleteGear = async (gearId) => {
    try {
      const response = await fetch(`${API}/gear/${gearId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        setGear(gear.filter(g => g.gear_id !== gearId));
        toast.success('Tack item deleted');
      } else {
        toast.error('Failed to delete tack');
      }
    } catch (error) {
      toast.error('Failed to delete tack');
    }
  };

  // Delete Document handler
  const handleDeleteDocument = async (documentId) => {
    try {
      const response = await fetch(`${API}/documents/${documentId}`, { method: 'DELETE', headers: getHeaders() });
      if (response.ok) {
        setDocuments(documents.filter(d => d.document_id !== documentId));
        toast.success('Document deleted');
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getRecordTypeIcon = (type) => {
    const icons = {
      vaccination: <Syringe className="w-4 h-4" />,
      farrier: <Scissors className="w-4 h-4" />,
      dentist: <Stethoscope className="w-4 h-4" />,
      worming: <Activity className="w-4 h-4" />,
      veterinary: <Stethoscope className="w-4 h-4" />,
      gelding: <Scissors className="w-4 h-4" />,
      medication: <Activity className="w-4 h-4" />,
      insemination: <Heart className="w-4 h-4" />,
      pregnancy_scan: <Search className="w-4 h-4" />,
      foaling: <Baby className="w-4 h-4" />
    };
    return icons[type] || <Activity className="w-4 h-4" />;
  };

  const getRecordTypeClass = (type) => `health-${type}`;
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading || !horse) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="h-8 w-32 skeleton rounded-lg" />
          <div className="h-64 skeleton rounded-xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8" data-testid="horse-profile">
        <Button variant="ghost" onClick={() => navigate('/horses')} className="mb-4 -ml-2" data-testid="back-to-horses-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Horses
        </Button>

        {/* Header Card */}
        <Card className="border-0 shadow-card bg-white mb-6 overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="relative w-full md:w-64 h-64 md:h-auto bg-muted flex-shrink-0">
              {horse.profile_photo ? (
                <img src={horse.profile_photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${horse.profile_photo}` : horse.profile_photo} alt={horse.stable_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-forest/10 to-saddle/10">
                  <span className="text-8xl">🐴</span>
                </div>
              )}
              <Button size="icon" variant="secondary" className="absolute bottom-4 right-4 rounded-full" onClick={() => fileInputRef.current?.click()} data-testid="upload-photo-btn">
                <Camera className="w-4 h-4" />
              </Button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
            </div>

            <div className="flex-1 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl md:text-3xl font-bold font-serif">{horse.stable_name}</h1>
                    <Badge className={horse.horse_tab === 'former' || horse.status === 'former' ? 'status-former' : 'status-current'}>
                      {horse.horse_tab === 'in_work' || (!horse.horse_tab && horse.status === 'current') ? 'In Work' :
                       horse.horse_tab === 'spelling' ? 'Spelling' :
                       horse.horse_tab === 'breeding' ? 'Breeding' :
                       horse.horse_tab === 'youngstock' ? 'Youngstock' :
                       horse.is_archived_transfer ? 'Transfer Archive' : 'Former'}
                    </Badge>
                    {horse.is_archived_transfer && (
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-muted-foreground">{horse.registered_name}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={() => setEditDialogOpen(true)} data-testid="edit-horse-btn"><Edit2 className="w-4 h-4" /></Button>
                  <Link to={`/horses/${horseId}/qr-profile`}>
                    <Button variant="outline" className="gap-2" data-testid="qr-profile-btn">
                      <QrCode className="w-4 h-4" />
                      <span className="hidden sm:inline">QR-ID Profile</span>
                    </Button>
                  </Link>
                  
                  {/* Archive / Reinstate Button */}
                  {horse.status === 'former' ? (
                    !horse.is_archived_transfer && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="gap-2 text-forest" data-testid="reinstate-horse-btn">
                            <RotateCcw className="w-4 h-4" />
                            <span className="hidden sm:inline">Reinstate</span>
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Reinstate {horse.stable_name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will move the horse back to your current horses.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleReinstateHorse} className="bg-forest hover:bg-forest-dark">
                              Reinstate
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )
                  ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="gap-2" data-testid="archive-horse-btn">
                          <Archive className="w-4 h-4" />
                          <span className="hidden sm:inline">Archive</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Archive {horse.stable_name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will move the horse to Former horses. The horse will no longer count towards your current horse limit.
                            You can reinstate the horse at any time.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleArchiveHorse} className="bg-saddle hover:bg-saddle-dark">
                            Archive
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  
                  {/* Transfer Button - only for current horses */}
                  {horse.status !== 'former' && (
                    <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="icon" className="text-forest" data-testid="transfer-horse-btn"><Send className="w-4 h-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Transfer {horse.stable_name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to transfer ownership of this horse to another user? 
                          This action will remove the horse from your account once the recipient accepts.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => setTransferDialogOpen(true)} className="bg-forest hover:bg-forest-dark">
                          Continue to Transfer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  )}
                  
                  {/* Delete button - not for transfer archive copies */}
                  {!horse.is_archived_transfer && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="icon" className="text-destructive" data-testid="delete-horse-btn"><Trash2 className="w-4 h-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete {horse.stable_name}?</AlertDialogTitle>
                        <AlertDialogDescription>This will permanently delete this horse and all associated records.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteHorse} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {horse.breed && <div><span className="text-muted-foreground">Breed</span><p className="font-medium">{horse.breed}</p></div>}
                {horse.colour && <div><span className="text-muted-foreground">Colour</span><p className="font-medium">{horse.colour}</p></div>}
                {horse.sex && <div><span className="text-muted-foreground">Sex</span><p className="font-medium capitalize">{horse.sex}</p></div>}
                {horse.dob && <div><span className="text-muted-foreground">DOB</span><p className="font-medium">{format(parseISO(horse.dob), 'MMM d, yyyy')}</p></div>}
                {horse.height && <div><span className="text-muted-foreground">Height</span><p className="font-medium">{horse.height} hh</p></div>}
                {horse.microchip && <div><span className="text-muted-foreground">Microchip</span><p className="font-medium font-mono text-xs">{horse.microchip}</p></div>}
                {horse.registration_number && <div><span className="text-muted-foreground">Reg. Number</span><p className="font-medium">{horse.registration_number}</p></div>}
                {horse.registration_society && <div><span className="text-muted-foreground">Reg. Society</span><p className="font-medium">{horse.registration_society}</p></div>}
                {horse.sire && <div><span className="text-muted-foreground">Sire</span><p className="font-medium">{horse.sire}</p></div>}
                {horse.dam && <div><span className="text-muted-foreground">Dam</span><p className="font-medium">{horse.dam}</p></div>}
                {horse.breeder && <div><span className="text-muted-foreground">Breeder</span><p className="font-medium">{horse.breeder}</p></div>}
                {horse.owner && <div><span className="text-muted-foreground">Owner</span><p className="font-medium">{horse.owner}</p></div>}
              </div>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-muted mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="feed">Feed</TabsTrigger>
            <TabsTrigger value="health">Health</TabsTrigger>
            <TabsTrigger value="training">Training</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="expenses">Expenses</TabsTrigger>
            <TabsTrigger value="show-prep">Show Prep</TabsTrigger>
            <TabsTrigger value="gear">Tack</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="photos">Photos</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-0 shadow-card bg-white"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-forest/10"><Activity className="w-5 h-5 text-forest" /></div><div><p className="text-2xl font-bold">{rideLogs.length}</p><p className="text-sm text-muted-foreground">Rides Logged</p></div></div></CardContent></Card>
              <Card className="border-0 shadow-card bg-white"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-saddle/10"><Stethoscope className="w-5 h-5 text-saddle" /></div><div><p className="text-2xl font-bold">{healthRecords.length}</p><p className="text-sm text-muted-foreground">Health Records</p></div></div></CardContent></Card>
              <Card className="border-0 shadow-card bg-white"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-terracotta/10"><Trophy className="w-5 h-5 text-terracotta" /></div><div><p className="text-2xl font-bold">{eventLogs.length}</p><p className="text-sm text-muted-foreground">Events</p></div></div></CardContent></Card>
              <Card className="border-0 shadow-card bg-white"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 rounded-lg bg-destructive/10"><DollarSign className="w-5 h-5 text-destructive" /></div><div><p className="text-2xl font-bold">${totalExpenses.toFixed(0)}</p><p className="text-sm text-muted-foreground">Total Expenses</p></div></div></CardContent></Card>
            </div>
            
            {/* Horse Category/Tab Selection */}
            <Card className="border-0 shadow-card bg-white">
              <CardContent className="p-4">
                <Label className="mb-2 block">Horse Category</Label>
                <Select 
                  value={horse.horse_tab || 'in_work'} 
                  onValueChange={async (newTab) => {
                    try {
                      const response = await fetch(`${API}/horses/${horseId}/change-tab?new_tab=${newTab}`, {
                        method: 'POST',
                        headers: getHeaders()
                      });
                      if (response.ok) {
                        const updated = await response.json();
                        setHorse(updated);
                        toast.success(`Moved to ${newTab === 'in_work' ? 'In Work' : newTab === 'spelling' ? 'Spelling' : newTab === 'breeding' ? 'Breeding' : newTab === 'youngstock' ? 'Youngstock' : 'Former'}`);
                      }
                    } catch (error) {
                      toast.error('Failed to change category');
                    }
                  }}
                  data-testid="horse-tab-select"
                >
                  <SelectTrigger className="w-full max-w-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_work">In Work</SelectItem>
                    <SelectItem value="spelling">Spelling</SelectItem>
                    <SelectItem value="breeding">Breeding</SelectItem>
                    <SelectItem value="youngstock">Youngstock</SelectItem>
                    <SelectItem value="former">Former (Archived)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  {horse.horse_tab === 'former' ? 'Archived horses appear in the Former tab' : 'Current horses appear in your active roster'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4" data-testid="history-tab">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold font-serif">Timeline</h2>
              <div className="flex gap-2">
                {horse.is_deceased ? (
                  <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="gap-2 text-forest border-forest hover:bg-forest/10" data-testid="restore-horse-btn">
                        <Heart className="w-4 h-4" /> Restore Horse
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Restore Horse</DialogTitle></DialogHeader>
                      <div className="space-y-4 mt-4">
                        <p className="text-sm text-muted-foreground">This will remove the deceased status and restore the horse to current status. The deceased event will be removed from the timeline.</p>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => setRestoreDialogOpen(false)} className="flex-1">Cancel</Button>
                          <Button onClick={handleUnmarkDeceased} className="flex-1 bg-forest hover:bg-forest-dark" data-testid="confirm-restore-btn">
                            Confirm Restore
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <Dialog open={deceasedDialogOpen} onOpenChange={(open) => { setDeceasedDialogOpen(open); if (!open) { setDeceasedConfirmStep(1); setDeceasedDate(''); } }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="gap-2 text-destructive border-destructive hover:bg-destructive/10" data-testid="mark-deceased-btn">
                        <Skull className="w-4 h-4" /> Mark Deceased
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Mark Horse as Deceased</DialogTitle></DialogHeader>
                      {deceasedConfirmStep === 1 ? (
                        <div className="space-y-4 mt-4">
                          <p className="text-sm text-muted-foreground">This will archive the horse and add a deceased entry to the timeline.</p>
                          <div className="space-y-2">
                            <Label>Date of Death *</Label>
                            <Input 
                              type="date" 
                              value={deceasedDate} 
                              onChange={(e) => setDeceasedDate(e.target.value)} 
                              data-testid="deceased-date-input"
                            />
                          </div>
                          <Button 
                            onClick={handleMarkDeceased} 
                            className="w-full"
                            variant="outline"
                            disabled={!deceasedDate}
                            data-testid="first-confirm-deceased-btn"
                          >
                            Continue
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-4 mt-4">
                          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                            <p className="text-sm font-medium text-destructive">Are you sure you want to mark this horse as deceased?</p>
                            <p className="text-sm text-muted-foreground mt-2">Date: {deceasedDate}</p>
                          </div>
                          <p className="text-sm text-muted-foreground">This action can be undone by clicking "Restore Horse".</p>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setDeceasedConfirmStep(1)} className="flex-1">Back</Button>
                            <Button 
                              onClick={handleMarkDeceased} 
                              className="flex-1 bg-destructive hover:bg-destructive/90"
                              data-testid="confirm-deceased-btn"
                            >
                              Confirm Deceased
                            </Button>
                          </div>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                )}
                <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-forest hover:bg-forest-dark gap-2" data-testid="add-history-btn">
                      <Plus className="w-4 h-4" /> Add Event
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Add History Event</DialogTitle></DialogHeader>
                    <HistoryEventForm data={newHistoryEvent} onChange={setNewHistoryEvent} onSubmit={handleAddHistoryEvent} />
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {timeline.length === 0 ? (
              <Card className="border-0 shadow-card bg-white">
                <CardContent className="py-12 text-center">
                  <History className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-muted-foreground">No timeline events yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Add important events to track this horse's history</p>
                </CardContent>
              </Card>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
                
                <div className="space-y-4">
                  {timeline.map((event, i) => {
                    const getEventIcon = () => {
                      switch (event.type) {
                        case 'birth': return <Baby className="w-5 h-5 text-pink-600" />;
                        case 'new_owner': return <User className="w-5 h-5 text-blue-600" />;
                        case 'international_travel': return <Plane className="w-5 h-5 text-sky-600" />;
                        case 'gelding': return <Scissors className="w-5 h-5 text-purple-600" />;
                        case 'broken_in': return <Activity className="w-5 h-5 text-orange-600" />;
                        case 'training': return <Activity className="w-5 h-5 text-amber-600" />;
                        case 'foaling': return <Heart className="w-5 h-5 text-red-500" />;
                        case 'deceased': return <Skull className="w-5 h-5 text-gray-600" />;
                        default: return <Calendar className="w-5 h-5 text-forest" />;
                      }
                    };

                    const getEventColor = () => {
                      switch (event.type) {
                        case 'birth': return 'bg-pink-100 border-pink-300';
                        case 'new_owner': return 'bg-blue-50 border-blue-200';
                        case 'international_travel': return 'bg-sky-50 border-sky-200';
                        case 'gelding': return 'bg-purple-50 border-purple-200';
                        case 'broken_in': return 'bg-orange-50 border-orange-200';
                        case 'training': return 'bg-amber-50 border-amber-200';
                        case 'foaling': return 'bg-red-50 border-red-200';
                        case 'deceased': return 'bg-gray-100 border-gray-300';
                        default: return 'bg-white border-border';
                      }
                    };

                    return (
                      <div key={event.event_id || `auto-${i}`} className="relative flex gap-4 animate-slideUp" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`timeline-event-${i}`}>
                        {/* Timeline dot */}
                        <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full bg-white border-2 border-border shadow-sm">
                          {getEventIcon()}
                        </div>

                        {/* Event card */}
                        <Card className={`flex-1 border shadow-card ${getEventColor()}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold">{event.title}</h3>
                                  {event.auto_generated && (
                                    <Badge variant="secondary" className="text-xs">Auto</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {event.date && format(parseISO(event.date), 'MMMM d, yyyy')}
                                </p>
                                {event.description && (
                                  <p className="text-sm mt-2">{event.description}</p>
                                )}
                                {event.type === 'foaling' && event.details && (
                                  <div className="mt-2 text-sm space-y-1">
                                    {event.details.foal_name && <p><span className="text-muted-foreground">Foal Name:</span> {event.details.foal_name}</p>}
                                    {event.details.sire_of_foal && <p><span className="text-muted-foreground">Sire:</span> {event.details.sire_of_foal}</p>}
                                    <p><span className="text-muted-foreground">Outcome:</span> {event.details.outcome === 'live_foal' ? 'Live Foal' : 'Stillbirth'}</p>
                                    {event.details.foal_sex && <p><span className="text-muted-foreground">Sex:</span> {event.details.foal_sex === 'colt' ? 'Colt' : 'Filly'}</p>}
                                    <p><span className="text-muted-foreground">Delivery:</span> {event.details.delivery_type === 'assisted' ? 'Assisted' : 'Normal'} - {event.details.foal_count === 'twin' ? 'Twin' : 'Single'}</p>
                                    {event.details.notes && <p className="mt-2 italic text-muted-foreground">{event.details.notes}</p>}
                                  </div>
                                )}
                                {/* Show notes for non-foaling events */}
                                {event.type !== 'foaling' && event.notes && (
                                  <p className="text-sm mt-2 italic text-muted-foreground">{event.notes}</p>
                                )}
                              </div>
                              {/* Show edit/delete for non-auto events OR for auto-generated new_owner events */}
                              {/* But hide for read_only events (from previous owner) */}
                              {!event.read_only && ((!event.auto_generated && event.event_id) || (event.auto_generated && event.type === 'new_owner')) && (
                                <div className="flex items-center gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => { 
                                      // For all new_owner events (including auto-generated transfer events), open Edit dialog
                                      // The save handler will create a new history event if event_id is null
                                      setEditingHistoryEvent({
                                        event_id: event.event_id || null, // null for auto-generated
                                        event_type: event.type || 'new_owner',
                                        date: event.date,
                                        from_country: event.from_country || '',
                                        to_country: event.to_country || '',
                                        trainer: event.trainer || '',
                                        owner_name: event.owner_name || '',
                                        foaling_details: event.details || null,
                                        description: event.type === 'other' ? event.description : (event.description || ''),
                                        notes: event.notes || '',
                                        transfer_id: event.transfer_id || null // Link to transfer record
                                      });
                                      setEditHistoryDialogOpen(true);
                                    }}
                                    data-testid={`edit-history-${event.event_id || `auto-${event.type}`}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  {!event.auto_generated && event.event_id && (
                                    <Button 
                                      variant="ghost" 
                                      size="icon" 
                                      className="text-destructive"
                                      onClick={() => handleDeleteHistoryEvent(event.event_id)}
                                      data-testid={`delete-history-${event.event_id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              )}
                              {/* Show read-only badge for events from previous owner */}
                              {event.read_only && (
                                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                  Previous owner
                                </span>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Feed Tab */}
          <TabsContent value="feed" className="space-y-4" data-testid="feed-tab">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold font-serif">Feed Schedule</h2>
              <Dialog open={feedDialogOpen} onOpenChange={setFeedDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-forest hover:bg-forest-dark gap-2" data-testid="add-feed-btn">
                    <Plus className="w-4 h-4" /> Add Entry
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Add Feed Entry</DialogTitle></DialogHeader>
                  <FeedForm data={newFeed} onChange={setNewFeed} onSubmit={handleAddFeed} />
                </DialogContent>
              </Dialog>
            </div>

            {feedEntries.length === 0 ? (
              <Card className="border-0 shadow-card bg-white">
                <CardContent className="py-12 text-center">
                  <Wheat className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-muted-foreground">No feed entries yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Add a feed schedule to track what this horse eats</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {feedEntries.map((entry, i) => (
                  <Card key={entry.feed_id} className="border-0 shadow-card bg-white animate-slideUp" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`feed-entry-${entry.feed_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-amber-100">
                            <Wheat className="w-5 h-5 text-amber-700" />
                          </div>
                          <div>
                            <CardTitle className="text-base font-medium">
                              {entry.date && format(parseISO(entry.date), 'MMMM d, yyyy')}
                            </CardTitle>
                            <p className="text-sm text-muted-foreground">{entry.items?.length || 0} item(s)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => { setEditingFeed(entry); setEditFeedDialogOpen(true); }}
                            data-testid={`edit-feed-${entry.feed_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-destructive" 
                            onClick={() => handleDeleteFeed(entry.feed_id)}
                            data-testid={`delete-feed-${entry.feed_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {/* Feed Items Table */}
                      <div className="border rounded-lg overflow-hidden mt-2">
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Item</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Amount</th>
                              <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">Frequency</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(entry.items || []).map((item, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/20'}>
                                <td className="px-4 py-2 text-sm font-medium">{item.item_name}</td>
                                <td className="px-4 py-2 text-sm text-muted-foreground">{item.amount}</td>
                                <td className="px-4 py-2">
                                  <Badge variant="outline" className="text-xs">
                                    {frequencyLabels[item.frequency] || item.frequency}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {entry.notes && (
                        <p className="mt-3 text-sm text-muted-foreground italic">{entry.notes}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Health Tab */}
          <TabsContent value="health" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold font-serif">Health Records</h2>
              <Dialog open={healthDialogOpen} onOpenChange={setHealthDialogOpen}>
                <DialogTrigger asChild><Button className="bg-forest hover:bg-forest-dark gap-2"><Plus className="w-4 h-4" /> Add Record</Button></DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Health Record</DialogTitle></DialogHeader><HealthForm data={newHealth} onChange={setNewHealth} onSubmit={handleAddHealth} /></DialogContent>
              </Dialog>
            </div>

            {healthRecords.length === 0 ? (
              <Card className="border-0 shadow-card bg-white"><CardContent className="py-12 text-center"><Stethoscope className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No health records yet</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {healthRecords.map((record, i) => (
                  <Card key={record.record_id} className="border-0 shadow-card bg-white animate-slideUp" style={{ animationDelay: `${i * 0.05}s` }}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${getRecordTypeClass(record.record_type)}`}>{getRecordTypeIcon(record.record_type)}</div>
                          <div>
                            <p className="font-medium capitalize">
                              {record.record_type === 'pregnancy_scan' ? 'Breeding Scan' : record.record_type}
                              {record.record_type === 'veterinary' && record.vet_visit_type && (
                                <span className="text-muted-foreground font-normal"> - {record.vet_visit_type.replace('_', ' ')}</span>
                              )}
                              {record.record_type === 'farrier' && record.farrier_type && (
                                <span className="text-muted-foreground font-normal"> - {record.farrier_type}</span>
                              )}
                              {record.record_type === 'insemination' && record.service_type && (
                                <span className="text-muted-foreground font-normal"> - {record.service_type.replace('ai_', 'AI ').replace('_', ' ')}</span>
                              )}
                              {record.record_type === 'pregnancy_scan' && record.scan_result && (
                                <span className={`font-normal ml-2 px-2 py-0.5 rounded-full text-xs ${
                                  record.scan_result === 'positive' ? 'bg-green-100 text-green-700' : 
                                  record.scan_result === 'negative' ? 'bg-red-100 text-red-700' : 
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {record.scan_result === 'positive' ? 'Positive Pregnancy' : 
                                   record.scan_result === 'negative' ? 'Negative Pregnancy' : 
                                   'Routine Scan'}
                                </span>
                              )}
                              {record.record_type === 'foaling' && record.outcome && (
                                <span className={`font-normal ml-2 px-2 py-0.5 rounded-full text-xs ${record.outcome === 'live_foal' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {record.outcome === 'live_foal' ? 'Live Foal' : 'Stillbirth'}
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {record.record_type === 'insemination' && record.sire ? `Sire: ${record.sire}` : 
                               record.record_type === 'foaling' && record.sire ? `Sire: ${record.sire}` :
                               record.provider || 'No provider'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right mr-2">
                            <p className="font-medium">{record.date && format(parseISO(record.date), 'MMM d, yyyy')}</p>
                            {record.next_due_date && <p className="text-sm text-muted-foreground">Next: {format(parseISO(record.next_due_date), 'MMM d, yyyy')}</p>}
                            {record.record_type === 'pregnancy_scan' && record.foal_due_date && (
                              <p className="text-sm text-pink-600 font-medium">Foal Due: {format(parseISO(record.foal_due_date), 'MMM d, yyyy')}</p>
                            )}
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => { setEditingHealth(record); setEditHealthDialogOpen(true); }} data-testid={`edit-health-${record.record_id}`}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteHealth(record.record_id)} data-testid={`delete-health-${record.record_id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      {/* Foaling details */}
                      {record.record_type === 'foaling' && (record.foal_name || record.foal_sex || record.delivery_type || record.number_of_foals) && (
                        <div className="mt-2 text-sm text-muted-foreground space-y-1">
                          {record.foal_name && <p><span className="font-medium">Foal Name:</span> {record.foal_name}</p>}
                          {record.foal_sex && <p><span className="font-medium">Sex:</span> {record.foal_sex === 'colt' ? 'Colt' : 'Filly'}</p>}
                          {(record.delivery_type || record.number_of_foals) && (
                            <p>
                              <span className="font-medium">Delivery:</span> {record.delivery_type === 'normal' ? 'Normal' : record.delivery_type === 'assisted' ? 'Assisted' : ''} 
                              {record.number_of_foals && ` - ${record.number_of_foals === 'single' ? 'Single' : 'Twin'}`}
                            </p>
                          )}
                        </div>
                      )}
                      {record.notes && <p className="mt-3 text-sm text-muted-foreground">{record.notes}</p>}
                      {/* Show photos for veterinary records */}
                      {record.record_type === 'veterinary' && record.photos && record.photos.length > 0 && (
                        <div className="mt-3 flex gap-2">
                          {record.photos.map((photoUrl, idx) => (
                            <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden bg-muted">
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${photoUrl}`} alt={`Vet photo ${idx + 1}`} className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Training Tab */}
          <TabsContent value="training" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold font-serif">Ride Logs</h2>
              <Dialog open={rideDialogOpen} onOpenChange={setRideDialogOpen}>
                <DialogTrigger asChild><Button className="bg-forest hover:bg-forest-dark gap-2"><Plus className="w-4 h-4" /> Log Ride</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Log Ride</DialogTitle></DialogHeader><RideForm data={newRide} onChange={setNewRide} onSubmit={handleAddRide} riders={riders} /></DialogContent>
              </Dialog>
            </div>

            {rideLogs.length === 0 ? (
              <Card className="border-0 shadow-card bg-white"><CardContent className="py-12 text-center"><Activity className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No rides logged yet</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {rideLogs.map((ride, i) => {
                  const rideTypes = ride.ride_types || (ride.ride_type ? [ride.ride_type] : []);
                  const displayTypes = rideTypes.length > 0 ? rideTypes.join(', ') : 'Training';
                  return (
                    <Card key={ride.ride_id} className="border-0 shadow-card bg-white animate-slideUp" style={{ animationDelay: `${i * 0.05}s` }}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-forest/10"><Activity className="w-5 h-5 text-forest" /></div>
                            <div>
                              <p className="font-medium capitalize">{displayTypes}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1"><User className="w-3 h-3" /> {ride.rider}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right mr-2">
                              <p className="font-medium">{ride.date && format(parseISO(ride.date), 'MMM d, yyyy')}</p>
                              {ride.duration && <p className="text-sm text-muted-foreground">{ride.duration} minutes</p>}
                            </div>
                          <Button variant="ghost" size="icon" onClick={() => { setEditingRide({...ride, duration: ride.duration || ''}); setEditRideDialogOpen(true); }}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteRide(ride.ride_id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      {ride.notes && <p className="mt-3 text-sm text-muted-foreground">{ride.notes}</p>}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold font-serif">Event Logs</h2>
              <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                <DialogTrigger asChild><Button className="bg-forest hover:bg-forest-dark gap-2"><Plus className="w-4 h-4" /> Add Event</Button></DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Add Event</DialogTitle></DialogHeader><EventForm data={newEvent} onChange={setNewEvent} onSubmit={handleAddEvent} /></DialogContent>
              </Dialog>
            </div>

            {eventLogs.length === 0 ? (
              <Card className="border-0 shadow-card bg-white"><CardContent className="py-12 text-center"><Trophy className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No events logged yet</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {eventLogs.map((event, i) => (
                  <Card key={event.event_id} className="border-0 shadow-card bg-white animate-slideUp" style={{ animationDelay: `${i * 0.05}s` }}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-terracotta/10"><Trophy className="w-5 h-5 text-terracotta" /></div>
                          <div><p className="font-medium">{event.event_name}</p>{event.location && <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.location}</p>}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right mr-2">
                            <p className="font-medium">{event.date && format(parseISO(event.date), 'MMM d, yyyy')}</p>
                            {event.placing && <Badge className="mt-1">{event.placing}</Badge>}
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => { setEditingEvent(event); setEditEventDialogOpen(true); }}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteEvent(event.event_id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      {(event.discipline || event.event_class || event.score) && <div className="mt-3 flex gap-4 text-sm text-muted-foreground">{event.discipline && <span>Discipline: {event.discipline}</span>}{event.event_class && <span>Class: {event.event_class}</span>}{event.score && <span>Score: {event.score}</span>}</div>}
                      {event.notes && <p className="mt-2 text-sm text-muted-foreground">{event.notes}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Expenses Tab */}
          <TabsContent value="expenses" className="space-y-4">
            <div className="flex justify-between items-center">
              <div><h2 className="text-xl font-semibold font-serif">Expenses</h2><p className="text-muted-foreground">Total: <span className="font-semibold text-terracotta">${totalExpenses.toFixed(2)}</span></p></div>
              <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
                <DialogTrigger asChild><Button className="bg-forest hover:bg-forest-dark gap-2"><Plus className="w-4 h-4" /> Add Expense</Button></DialogTrigger>
                <DialogContent><DialogHeader><DialogTitle>Add Expense</DialogTitle></DialogHeader><ExpenseForm data={newExpense} onChange={setNewExpense} onSubmit={handleAddExpense} /></DialogContent>
              </Dialog>
            </div>

            {expenses.length === 0 ? (
              <Card className="border-0 shadow-card bg-white"><CardContent className="py-12 text-center"><DollarSign className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No expenses recorded yet</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {expenses.map((expense, i) => (
                  <Card key={expense.expense_id} className="border-0 shadow-card bg-white animate-slideUp" style={{ animationDelay: `${i * 0.05}s` }}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-terracotta/10"><DollarSign className="w-5 h-5 text-terracotta" /></div>
                          <div><p className="font-medium capitalize">{expense.category?.replace('_', ' ')}</p>{expense.vendor && <p className="text-sm text-muted-foreground">{expense.vendor}</p>}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right mr-2">
                            <p className="font-semibold text-lg text-terracotta">${expense.amount?.toFixed(2)}</p>
                            <p className="text-sm text-muted-foreground">{expense.date && format(parseISO(expense.date), 'MMM d, yyyy')}</p>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => { setEditingExpense({...expense, amount: expense.amount || ''}); setEditExpenseDialogOpen(true); }}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteExpense(expense.expense_id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      {expense.notes && <p className="mt-3 text-sm text-muted-foreground">{expense.notes}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Show Prep Tab */}
          <TabsContent value="show-prep" className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <h2 className="text-xl font-semibold font-serif">Show Prep Checklist</h2>
              <Button 
                variant="outline" 
                onClick={handleClearShowPrepChecklist}
                disabled={!showPrep.items?.some(item => item.checked)}
                data-testid="clear-checklist-btn"
              >
                Clear All Checkboxes
              </Button>
            </div>
            
            {/* Add new item */}
            <Card className="border-0 shadow-card bg-white">
              <CardContent className="p-4">
                <div className="flex gap-2">
                  <Input 
                    placeholder="Add new item..."
                    value={newShowPrepItem}
                    onChange={(e) => setNewShowPrepItem(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddShowPrepItem())}
                    data-testid="new-show-prep-item-input"
                  />
                  <Button 
                    onClick={handleAddShowPrepItem} 
                    className="bg-forest hover:bg-forest-dark"
                    data-testid="add-show-prep-item-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Checklist */}
            <Card className="border-0 shadow-card bg-white">
              <CardContent className="p-4">
                {showPrep.items?.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No items in checklist</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {showPrep.items?.map((item, i) => (
                      <div 
                        key={item.item_id} 
                        className={`flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors animate-slideUp ${item.checked ? 'bg-forest/5' : ''}`}
                        style={{ animationDelay: `${i * 0.02}s` }}
                      >
                        <Checkbox
                          checked={item.checked}
                          onCheckedChange={() => handleToggleShowPrepItem(item.item_id)}
                          data-testid={`show-prep-checkbox-${item.item_id}`}
                        />
                        <span className={`flex-1 ${item.checked ? 'line-through text-muted-foreground' : ''}`}>
                          {item.name}
                        </span>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              data-testid={`delete-show-prep-item-${item.item_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove Item</AlertDialogTitle>
                              <AlertDialogDescription>
                                Remove "{item.name}" from the checklist?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteShowPrepItem(item.item_id)}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Progress indicator */}
                {showPrep.items?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        {showPrep.items.filter(i => i.checked).length} / {showPrep.items.length} items
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-forest transition-all duration-300"
                        style={{ width: `${(showPrep.items.filter(i => i.checked).length / showPrep.items.length) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tack Tab */}
          <TabsContent value="gear" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold font-serif">Tack</h2>
              <Dialog open={gearDialogOpen} onOpenChange={setGearDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-forest hover:bg-forest-dark gap-2" data-testid="add-gear-btn">
                    <Plus className="w-4 h-4" /> Add Tack
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Add Tack for {horse.stable_name}</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddGear} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Item Name *</Label>
                      <Input value={newGear.item_name} onChange={(e) => setNewGear({...newGear, item_name: e.target.value})} placeholder="e.g., Leather Saddle" required data-testid="gear-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Tack Type</Label>
                      <Select value={newGear.tack_type || ''} onValueChange={(v) => setNewGear({...newGear, tack_type: v})}>
                        <SelectTrigger data-testid="gear-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="saddle">Saddle</SelectItem>
                          <SelectItem value="bridle">Bridle</SelectItem>
                          <SelectItem value="cover">Cover</SelectItem>
                          <SelectItem value="girth">Girth</SelectItem>
                          <SelectItem value="saddle_pad">Saddle Pad</SelectItem>
                          <SelectItem value="horse_boots">Horse Boots</SelectItem>
                          <SelectItem value="halter_lead">Halter/Lead</SelectItem>
                          <SelectItem value="transport">Transport</SelectItem>
                          <SelectItem value="rider_apparel">Rider Apparel</SelectItem>
                          <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Size</Label>
                      <Input value={newGear.size || ''} onChange={(e) => setNewGear({...newGear, size: e.target.value})} placeholder="e.g., 17.5 inch, Medium" data-testid="gear-size-input" />
                    </div>
                    {newGear.tack_type === 'saddle' && (
                      <div className="space-y-2">
                        <Label>Serial Number</Label>
                        <Input value={newGear.serial_number || ''} onChange={(e) => setNewGear({...newGear, serial_number: e.target.value})} placeholder="Saddle serial number" data-testid="gear-serial-input" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Date Purchased</Label>
                      <Input type="date" value={newGear.purchase_date} onChange={(e) => setNewGear({...newGear, purchase_date: e.target.value})} data-testid="gear-date-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Purchase Location</Label>
                      <Input value={newGear.purchase_location} onChange={(e) => setNewGear({...newGear, purchase_location: e.target.value})} placeholder="e.g., Dover Saddlery" data-testid="gear-location-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Price</Label>
                      <Input type="number" step="0.01" value={newGear.cost} onChange={(e) => setNewGear({...newGear, cost: e.target.value})} placeholder="0.00" data-testid="gear-cost-input" />
                    </div>
                    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-gear-btn">Add Tack</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {gear.length === 0 ? (
              <Card className="border-0 shadow-card bg-white"><CardContent className="py-12 text-center"><Package className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No tack assigned to this horse</p><p className="text-sm text-muted-foreground mt-1">Click "Add Tack" to add items</p></CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {gear.map((item, i) => (
                  <Card key={item.gear_id} className="border-0 shadow-card bg-white animate-slideUp" style={{ animationDelay: `${i * 0.05}s` }}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{item.item_name}</p>
                          {item.tack_type && <p className="text-xs text-muted-foreground capitalize">{item.tack_type.replace('_', ' ')}</p>}
                          {item.size && <p className="text-xs text-muted-foreground">Size: {item.size}</p>}
                          {item.purchase_location && <p className="text-sm text-muted-foreground">{item.purchase_location}</p>}
                          {item.purchase_date && <p className="text-xs text-muted-foreground">{format(parseISO(item.purchase_date), 'MMM d, yyyy')}</p>}
                          {item.cost && <p className="text-sm font-semibold mt-2 text-forest">${item.cost.toFixed(2)}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => { setEditingGear(item); setEditGearDialogOpen(true); }} 
                            data-testid={`edit-gear-${item.gear_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteGear(item.gear_id)} data-testid={`delete-gear-${item.gear_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Edit Gear Dialog */}
            <Dialog open={editGearDialogOpen} onOpenChange={(open) => { setEditGearDialogOpen(open); if (!open) setEditingGear(null); }}>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Edit Tack</DialogTitle></DialogHeader>
                {editingGear && (
                  <form onSubmit={handleEditGear} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Item Name *</Label>
                      <Input value={editingGear.item_name} onChange={(e) => setEditingGear({...editingGear, item_name: e.target.value})} placeholder="e.g., Leather Saddle" required data-testid="edit-gear-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Tack Type</Label>
                      <Select value={editingGear.tack_type || ''} onValueChange={(v) => setEditingGear({...editingGear, tack_type: v})}>
                        <SelectTrigger data-testid="edit-gear-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="saddle">Saddle</SelectItem>
                          <SelectItem value="bridle">Bridle</SelectItem>
                          <SelectItem value="cover">Cover</SelectItem>
                          <SelectItem value="girth">Girth</SelectItem>
                          <SelectItem value="saddle_pad">Saddle Pad</SelectItem>
                          <SelectItem value="horse_boots">Horse Boots</SelectItem>
                          <SelectItem value="halter_lead">Halter/Lead</SelectItem>
                          <SelectItem value="transport">Transport</SelectItem>
                          <SelectItem value="rider_apparel">Rider Apparel</SelectItem>
                          <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Size</Label>
                      <Input value={editingGear.size || ''} onChange={(e) => setEditingGear({...editingGear, size: e.target.value})} placeholder="e.g., 17.5 inch, Medium" data-testid="edit-gear-size-input" />
                    </div>
                    {editingGear.tack_type === 'saddle' && (
                      <div className="space-y-2">
                        <Label>Serial Number</Label>
                        <Input value={editingGear.serial_number || ''} onChange={(e) => setEditingGear({...editingGear, serial_number: e.target.value})} placeholder="Saddle serial number" data-testid="edit-gear-serial-input" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Date Purchased</Label>
                      <Input type="date" value={editingGear.purchase_date || ''} onChange={(e) => setEditingGear({...editingGear, purchase_date: e.target.value})} data-testid="edit-gear-date-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Purchase Location</Label>
                      <Input value={editingGear.purchase_location || ''} onChange={(e) => setEditingGear({...editingGear, purchase_location: e.target.value})} placeholder="e.g., Dover Saddlery" data-testid="edit-gear-location-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Price</Label>
                      <Input type="number" step="0.01" value={editingGear.cost || ''} onChange={(e) => setEditingGear({...editingGear, cost: e.target.value})} placeholder="0.00" data-testid="edit-gear-cost-input" />
                    </div>
                    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-edit-gear-btn">Update Tack</Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold font-serif">Documents</h2>
              <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-forest hover:bg-forest-dark gap-2" data-testid="add-document-btn">
                    <Plus className="w-4 h-4" /> Add Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Add Document for {horse.stable_name}</DialogTitle></DialogHeader>
                  <form onSubmit={handleAddDocument} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input value={newDocument.title} onChange={(e) => setNewDocument({...newDocument, title: e.target.value})} placeholder="e.g., 2024 Registration Papers" required data-testid="doc-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Document Type</Label>
                      <Select value={newDocument.document_type} onValueChange={(v) => setNewDocument({...newDocument, document_type: v, custom_type: v === 'other' ? newDocument.custom_type : ''})}>
                        <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="registration">Registration</SelectItem>
                          <SelectItem value="vet_report">Vet Report</SelectItem>
                          <SelectItem value="contract">Contract</SelectItem>
                          <SelectItem value="insurance">Insurance</SelectItem>
                          <SelectItem value="passport">Passport</SelectItem>
                          <SelectItem value="xray">X-Ray</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newDocument.document_type === 'other' && (
                      <div className="space-y-2">
                        <Label>Custom Type Label *</Label>
                        <Input value={newDocument.custom_type || ''} onChange={(e) => setNewDocument({...newDocument, custom_type: e.target.value})} placeholder="Enter custom document type" required data-testid="doc-custom-type-input" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>File (optional)</Label>
                      <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-forest transition-colors ${selectedDocumentFile ? 'border-forest bg-forest/5' : 'border-muted'}`} onClick={() => documentFileInputRef.current?.click()}>
                        <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {selectedDocumentFile ? <span className="text-forest font-medium">{selectedDocumentFile.name}</span> : 'Click to upload file'}
                        </p>
                      </div>
                      <input ref={documentFileInputRef} type="file" onChange={(e) => setSelectedDocumentFile(e.target.files?.[0] || null)} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                    </div>
                    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-doc-btn">Add Document</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {documents.length === 0 ? (
              <Card className="border-0 shadow-card bg-white"><CardContent className="py-12 text-center"><FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No documents uploaded yet</p><p className="text-sm text-muted-foreground mt-1">Click "Add Document" to upload files</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {documents.map((doc, i) => (
                  <Card key={doc.document_id} className="border-0 shadow-card bg-white animate-slideUp" style={{ animationDelay: `${i * 0.05}s` }}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{doc.title}</p>
                          <p className="text-sm text-muted-foreground capitalize">{doc.document_type.replace('_', ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.file_url && <Button variant="outline" size="sm" asChild><a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer">View</a></Button>}
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteDocument(doc.document_id)} data-testid={`delete-doc-${doc.document_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos" className="space-y-4" data-testid="photos-tab">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold font-serif">Photos</h2>
                <p className="text-sm text-muted-foreground">{photos.length} / 20 photos</p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Caption (optional)"
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                  className="w-48"
                  data-testid="photo-caption-input"
                />
                <Button 
                  onClick={() => photoInputRef.current?.click()} 
                  className="bg-forest hover:bg-forest-dark gap-2"
                  disabled={uploadingPhoto || photos.length >= 20}
                  data-testid="upload-photo-btn"
                >
                  <Camera className="w-4 h-4" />
                  {uploadingPhoto ? 'Uploading...' : photos.length >= 20 ? 'Max 20 Photos' : 'Upload Photo'}
                </Button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleGalleryPhotoUpload}
                  className="hidden"
                />
              </div>
            </div>
            {photos.length === 0 ? (
              <Card className="border-0 shadow-card bg-white">
                <CardContent className="py-12 text-center">
                  <Image className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                  <p className="text-muted-foreground">No photos uploaded yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Click "Upload Photo" to add photos of your horse</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((photo, index) => (
                  <div key={photo.photo_id} className="relative aspect-square rounded-lg overflow-hidden shadow-card bg-white group" data-testid={`photo-${photo.photo_id}`}>
                    <img 
                      src={photo.photo_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${photo.photo_url}` : photo.photo_url} 
                      alt={photo.caption || 'Horse photo'} 
                      className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                      onClick={() => {
                        setLightboxPhoto(photo);
                        setLightboxIndex(index);
                        setLightboxOpen(true);
                      }}
                    />
                    {/* Zoom indicator on hover */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-white/90 hover:bg-white text-destructive shadow-sm z-10"
                          data-testid={`delete-photo-${photo.photo_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Photo</AlertDialogTitle>
                          <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeletePhoto(photo.photo_id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    {photo.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-sm p-2 truncate">
                        {photo.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Photo Lightbox Dialog */}
            <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
              <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-0">
                <div className="relative w-full h-full flex items-center justify-center min-h-[60vh]">
                  {/* Close button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 z-20 bg-black/50 hover:bg-black/70 text-white"
                    onClick={() => setLightboxOpen(false)}
                  >
                    <X className="w-6 h-6" />
                  </Button>

                  {/* Previous button */}
                  {photos.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white h-12 w-12"
                      onClick={() => {
                        const newIndex = lightboxIndex === 0 ? photos.length - 1 : lightboxIndex - 1;
                        setLightboxIndex(newIndex);
                        setLightboxPhoto(photos[newIndex]);
                      }}
                    >
                      <ChevronLeft className="w-8 h-8" />
                    </Button>
                  )}

                  {/* Main image */}
                  {lightboxPhoto && (
                    <img
                      src={lightboxPhoto.photo_url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${lightboxPhoto.photo_url}` : lightboxPhoto.photo_url}
                      alt={lightboxPhoto.caption || 'Horse photo'}
                      className="max-w-full max-h-[85vh] object-contain"
                    />
                  )}

                  {/* Next button */}
                  {photos.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white h-12 w-12"
                      onClick={() => {
                        const newIndex = lightboxIndex === photos.length - 1 ? 0 : lightboxIndex + 1;
                        setLightboxIndex(newIndex);
                        setLightboxPhoto(photos[newIndex]);
                      }}
                    >
                      <ChevronRight className="w-8 h-8" />
                    </Button>
                  )}

                  {/* Caption and counter */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
                    <div className="flex items-center justify-between">
                      <p className="text-lg">{lightboxPhoto?.caption || ''}</p>
                      <p className="text-sm text-white/70">{lightboxIndex + 1} / {photos.length}</p>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>

        {/* Transfer Dialog */}
        <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Transfer {horse.stable_name}</DialogTitle></DialogHeader>
            <form onSubmit={handleTransfer} className="space-y-4 mt-4">
              <div className="space-y-2"><Label>New Owner Email *</Label><Input type="email" value={transferEmail} onChange={(e) => setTransferEmail(e.target.value)} placeholder="newowner@example.com" required /></div>
              <p className="text-sm text-muted-foreground">The new owner must have an EquiVault account. They will receive a transfer request to accept or decline.</p>
              <Button type="submit" className="w-full bg-forest hover:bg-forest-dark">Send Transfer Request</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Horse Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit {horse.stable_name}</DialogTitle></DialogHeader>
            <form onSubmit={handleUpdateHorse} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2"><Label>Stable Name</Label><Input value={editData.stable_name || ''} onChange={(e) => setEditData({ ...editData, stable_name: e.target.value })} /></div>
                <div className="col-span-2 space-y-2"><Label>Registered Name</Label><Input value={editData.registered_name || ''} onChange={(e) => setEditData({ ...editData, registered_name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Breed</Label><Input value={editData.breed || ''} onChange={(e) => setEditData({ ...editData, breed: e.target.value })} /></div>
                <div className="space-y-2"><Label>Colour</Label><Input value={editData.colour || ''} onChange={(e) => setEditData({ ...editData, colour: e.target.value })} /></div>
                <div className="space-y-2"><Label>Sex</Label>
                  <Select value={editData.sex || ''} onValueChange={(v) => setEditData({ ...editData, sex: v })}>
                    <SelectTrigger><SelectValue placeholder="Select sex" /></SelectTrigger>
                    <SelectContent><SelectItem value="mare">Mare</SelectItem><SelectItem value="gelding">Gelding</SelectItem><SelectItem value="stallion">Stallion</SelectItem><SelectItem value="colt">Colt</SelectItem><SelectItem value="filly">Filly</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Date of Birth</Label><Input type="date" value={editData.dob || ''} onChange={(e) => setEditData({ ...editData, dob: e.target.value })} /></div>
                <div className="space-y-2"><Label>Height (hh)</Label><Input value={editData.height || ''} onChange={(e) => setEditData({ ...editData, height: e.target.value })} /></div>
                <div className="space-y-2"><Label>Microchip</Label><Input value={editData.microchip || ''} onChange={(e) => setEditData({ ...editData, microchip: e.target.value })} /></div>
                <div className="col-span-2 space-y-2"><Label>Registration Number</Label><Input value={editData.registration_number || ''} onChange={(e) => setEditData({ ...editData, registration_number: e.target.value })} /></div>
                <div className="col-span-2 space-y-2"><Label>Registration Society</Label><Input value={editData.registration_society || ''} onChange={(e) => setEditData({ ...editData, registration_society: e.target.value })} placeholder="e.g., AQHA, Jockey Club, USEF" /></div>
                <div className="space-y-2"><Label>Sire (Father)</Label><Input value={editData.sire || ''} onChange={(e) => setEditData({ ...editData, sire: e.target.value })} /></div>
                <div className="space-y-2"><Label>Dam (Mother)</Label><Input value={editData.dam || ''} onChange={(e) => setEditData({ ...editData, dam: e.target.value })} /></div>
                <div className="space-y-2"><Label>Breeder</Label><Input value={editData.breeder || ''} onChange={(e) => setEditData({ ...editData, breeder: e.target.value })} placeholder="Breeder name or farm" /></div>
                <div className="space-y-2"><Label>Owner</Label><Input value={editData.owner || ''} onChange={(e) => setEditData({ ...editData, owner: e.target.value })} placeholder="Owner's name" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-4"><Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button><Button type="submit" className="bg-forest hover:bg-forest-dark">Save Changes</Button></div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Health Dialog */}
        <Dialog open={editHealthDialogOpen} onOpenChange={(open) => { setEditHealthDialogOpen(open); if (!open) setEditingHealth(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Health Record</DialogTitle></DialogHeader>{editingHealth && <HealthForm data={editingHealth} onChange={setEditingHealth} onSubmit={handleEditHealth} isEdit={true} onPhotoUpload={() => handleHealthPhotoUpload(editingHealth.record_id)} photoUploading={uploadingHealthPhoto} />}</DialogContent>
        </Dialog>

        {/* Edit Ride Dialog */}
        <Dialog open={editRideDialogOpen} onOpenChange={(open) => { setEditRideDialogOpen(open); if (!open) setEditingRide(null); }}>
          <DialogContent><DialogHeader><DialogTitle>Edit Ride</DialogTitle></DialogHeader>{editingRide && <RideForm data={editingRide} onChange={setEditingRide} onSubmit={handleEditRide} isEdit={true} riders={riders} />}</DialogContent>
        </Dialog>

        {/* Edit Event Dialog */}
        <Dialog open={editEventDialogOpen} onOpenChange={(open) => { setEditEventDialogOpen(open); if (!open) setEditingEvent(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>Edit Event</DialogTitle></DialogHeader>{editingEvent && <EventForm data={editingEvent} onChange={setEditingEvent} onSubmit={handleEditEvent} isEdit={true} />}</DialogContent>
        </Dialog>

        {/* Edit Expense Dialog */}
        <Dialog open={editExpenseDialogOpen} onOpenChange={(open) => { setEditExpenseDialogOpen(open); if (!open) setEditingExpense(null); }}>
          <DialogContent><DialogHeader><DialogTitle>Edit Expense</DialogTitle></DialogHeader>{editingExpense && <ExpenseForm data={editingExpense} onChange={setEditingExpense} onSubmit={handleEditExpense} isEdit={true} />}</DialogContent>
        </Dialog>

        {/* Edit Feed Dialog */}
        <Dialog open={editFeedDialogOpen} onOpenChange={(open) => { setEditFeedDialogOpen(open); if (!open) { setEditingFeed(null); setFeedSaveAsNew(false); } }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Feed Entry</DialogTitle></DialogHeader>
            {editingFeed && <FeedForm data={editingFeed} onChange={setEditingFeed} onSubmit={handleEditFeed} isEdit={true} saveAsNew={feedSaveAsNew} setSaveAsNew={setFeedSaveAsNew} />}
          </DialogContent>
        </Dialog>

        {/* Edit History Event Dialog */}
        <Dialog open={editHistoryDialogOpen} onOpenChange={(open) => { setEditHistoryDialogOpen(open); if (!open) setEditingHistoryEvent(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit History Event</DialogTitle></DialogHeader>
            {editingHistoryEvent && <HistoryEventForm data={editingHistoryEvent} onChange={setEditingHistoryEvent} onSubmit={handleEditHistoryEvent} isEdit={true} />}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
